"use client"

import { useEffect, useMemo, useState } from "react"
import Image from "@/components/ui/image"
import { useLocale } from "@/lib/i18n-client"
import { useComponentsTranslations } from "@/hooks/use-translations"
import { useAuthStore } from "@/stores"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  LATEST_CHANGELOG_ENTRY,
  resolveChangelogLocale,
} from "@/lib/changelog"

const SEEN_VERSION_KEY = "blitztask_whats_new_seen_version"

export function WhatsNewModal() {
  const t = useComponentsTranslations()
  const locale = useLocale()
  const user = useAuthStore((s) => s.user)
  const [open, setOpen] = useState(false)
  const [pageIndex, setPageIndex] = useState(0)

  const changelogLocale = useMemo(
    () => resolveChangelogLocale(locale),
    [locale]
  )

  const entry = LATEST_CHANGELOG_ENTRY
  const pages = entry.pages
  const page = pages[pageIndex]
  const title = page.title[changelogLocale]
  const description = page.description?.[changelogLocale]
  const changes = page.changes?.[changelogLocale] ?? []
  const isFirstPage = pageIndex === 0
  const isLastPage = pageIndex === pages.length - 1
  const publishedAt = entry.publishedAt[changelogLocale]

  const markSeen = () => {
    try {
      localStorage.setItem(SEEN_VERSION_KEY, entry.version)
    } catch {
      // Ignore localStorage errors
    }
  }

  useEffect(() => {
    if (!user) return

    try {
      const seenVersion = localStorage.getItem(SEEN_VERSION_KEY)
      if (seenVersion === entry.version) return
    } catch {
      return
    }

    const timer = setTimeout(() => setOpen(true), 900)
    return () => clearTimeout(timer)
  }, [entry.version, user])

  const handleClose = () => {
    setOpen(false)
    setPageIndex(0)
    markSeen()
  }

  const handleNext = () => {
    setPageIndex((prev) => Math.min(prev + 1, pages.length - 1))
  }

  const handleBack = () => {
    setPageIndex((prev) => Math.max(prev - 1, 0))
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          handleClose()
        }
      }}
    >
      <DialogContent className="w-[calc(100vw-1.5rem)] max-w-xl gap-3 p-4 sm:w-full sm:gap-4 sm:p-6">
        <DialogHeader>
          <DialogTitle>{t("whats_new_title")}</DialogTitle>
          <DialogDescription>
            {t("whats_new_version_label")}: {entry.version} - {publishedAt}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 overflow-y-auto pr-1">
          {page.thumbnail ? (
            <div className="overflow-hidden rounded-md border border-border/60">
              <Image
                src={page.thumbnail}
                alt={t("whats_new_thumbnail_alt")}
                width={960}
                height={540}
                className="aspect-[16/9] h-auto w-full object-cover"
              />
            </div>
          ) : null}

          <p className="text-base font-medium">{title}</p>
          {description ? (
            <p className="text-sm text-muted-foreground">{description}</p>
          ) : null}

          {changes.length > 0 ? (
            <ul className="list-disc space-y-2 pl-5 text-sm text-muted-foreground">
              {changes.map((change, index) => (
                <li key={`${entry.version}-${pageIndex}-${index}`}>{change}</li>
              ))}
            </ul>
          ) : null}
        </div>

        <DialogFooter className="mt-1 flex-col gap-3 border-t border-border/60 pt-3 sm:mt-2 sm:flex-row sm:items-center sm:justify-between sm:gap-2 sm:pt-4">
          <p className="text-center text-xs text-muted-foreground sm:text-left">
            {t("whats_new_page_counter", {
              current: pageIndex + 1,
              total: pages.length,
            })}
          </p>
          <div className="grid w-full grid-cols-2 gap-2 sm:flex sm:w-auto sm:items-center">
            <Button
              variant="ghost"
              className="w-full sm:w-auto"
              onClick={handleBack}
              disabled={isFirstPage}
            >
              {t("whats_new_back")}
            </Button>
            {isLastPage ? (
              <Button className="w-full sm:w-auto" onClick={handleClose}>
                {t("whats_new_close")}
              </Button>
            ) : (
              <Button className="w-full sm:w-auto" onClick={handleNext}>
                {t("whats_new_next")}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
